/*
 * Copyright (c) 2019 Signum+ Software & Facility GmbH
 * Author: jahn@signum-plus.at
 */

import { formatValue } from '../helper/format-value.helper';
import { CoreErrorKey } from '../key/core-error.key';
import { ApplicationError } from '../model/application-error.model';
import { TextPattern } from '../model/text-pattern.model';
import { TextProvider } from '../provider/text.provider';

export class TextService {

  getText(key: string, args?: object): string {
    if (!key) {
      return key;
    }
    const argumentsMatch = /^([^?]+)(\?\S.*)$/.exec(key);
    if (!argumentsMatch) {
      return this.formatText(this.getPattern(key) || key, args);
    }
    const keyArgs = {};
    const argumentRegex = /[?&]([^=&]+)(?:=([^&]+))?/g;
    let argumentMatch = argumentRegex.exec(argumentsMatch[2]);
    while (argumentMatch !== null) {
      keyArgs[argumentMatch[1]] = argumentMatch[2] || true;
      // keyArgs[decodeURIComponent(argumentMatch[1])] = decodeURIComponent(argumentMatch[2]) || true;
      argumentMatch = argumentRegex.exec(argumentsMatch[2]);
    }
    return this.formatText(this.getPattern(argumentsMatch[1]) || argumentsMatch[1], Object.assign(keyArgs, args));
  }

  formatText(pattern: string, args?: object): string {
    if (!pattern) {
      return pattern;
    }
    const text = pattern
      .replace(
        /\#\{\s*([^\s}:]+)(?:\s*[:]\s*([^\s:}]+))?\s*\}/g,
        (match: string, placeholderExpression: string, placeholderFormat: string) => {
          let argument: any;
          if (!args) {
            argument = undefined;
          } else if (args[placeholderExpression] !== undefined) {
            argument = args[placeholderExpression];
          } else {
            const placeholderAlternatives = placeholderExpression.split('|');
            for (const placeholder of placeholderAlternatives) {
              if (args[placeholder] !== undefined) {
                argument = args[placeholder];
                break;
              }
              const segmentRegex = /^([^.]+)\.(.+)$/;
              let segmentMatch = segmentRegex.exec(placeholder);
              let segmentValue = segmentMatch && args[segmentMatch[1]];
              while (segmentValue && typeof segmentValue === 'object') {
                if (segmentValue[segmentMatch[2]] !== undefined) {
                  argument = segmentValue[segmentMatch[2]];
                  break;
                }
                segmentMatch = segmentRegex.exec(segmentMatch[2]);
                segmentValue = segmentMatch && segmentValue[segmentMatch[1]];
              }
              if (argument !== undefined) {
                break;
              }
            }
          }
          if (placeholderFormat) {
            return formatValue(argument, placeholderFormat);
          }
          if (argument === undefined) {
            return match;
          }
          if (argument === null) {
            return '';
          }
          if (typeof argument === 'string') {
            return argument;
          }
          if (typeof argument === 'object') {
            return JSON.stringify(argument);
          }
          return argument.toString();
        }
      )
      .replace(
        /\#\(\s*(\S+)\s*\)/g,
        (match: string, textKey: string) => {
          return this.getText(textKey, args);
        }
      );
    try {
      return decodeURIComponent(text);
    } catch {
      return text;
    }
  }

  getPattern(key: string) {
    if (!key) {
      return null;
    }
    const keyMatch = /^([^?]+)(?:[?].+)?$/.exec(key);
    // if (!keyMatch) {
    //   throw new ApplicationError({
    //     key: CoreErrorKey.TEXT_KEY_INVALID,
    //     arguments: { key },
    //     message: `Key '${key}' invalid`
    //   });
    // }
    const keySegments: string[] = keyMatch ? keyMatch[1].split('/') : [key];
    let patterns: TextPattern = TextProvider.resolveTextPatterns(keySegments[0]);
    for (let i = 1; i < keySegments.length; i++) {
      if (!patterns) {
        return null;
      }
      const keySegment = keySegments[i];
      if (typeof patterns !== 'object' || !keySegment) {
        throw new ApplicationError({
          key: CoreErrorKey.TEXT_KEY_INVALID,
          arguments: { key },
          message: `Key '${key}' invalid`
        });
      }
      patterns = patterns[keySegment];
    }
    if (typeof patterns === 'string') {
      return patterns;
    }
    if (!patterns) {
      return null;
    }
    throw new ApplicationError({
      key: CoreErrorKey.TEXT_KEY_INCOMPLETE,
      arguments: { key },
      message: `Key '${key}' incomplete`
    });
  }

}
